import { useState, useEffect } from 'react';
import type { ClawerContact, Draft, Message, Nudge, TimelineEvent, Tag, TrustLevel } from '../types';
import { contactsAPI, draftsAPI, nudgesAPI, outboundAPI, tagsAPI } from '../api/client';
import { captureEvent } from '../lib/posthog';
import { getBadgeStyle } from './ui/styles';
import { formatRelativeTime } from './ContactListCard';
import { LanguageBadge } from './LanguageBadge';
import { MessageBubble } from './MessageBubble';
import { MessageComposer } from './MessageComposer';

type DetailTab = 'profile' | 'messages' | 'facts' | 'timeline';

interface ContactDetailDrawerProps {
  contact: ClawerContact;
  nudge?: Nudge | null;
  latestDraft?: Draft | null;
  onClose?: () => void;
  onTagsChanged?: (contact: ClawerContact) => void;
  onTrustLevelChanged?: (contact: ClawerContact) => void;
  userLanguage?: string;
}

export function ContactDetailDrawer({
  contact,
  nudge = null,
  latestDraft = null,
  onClose: _onClose,
  onTagsChanged,
  onTrustLevelChanged,
  userLanguage = 'en',
}: ContactDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>('profile');
  const [detailContact, setDetailContact] = useState<ClawerContact>(contact);
  const [currentNudge, setCurrentNudge] = useState<Nudge | null>(nudge);
  const [draftHistory, setDraftHistory] = useState<Draft[]>(latestDraft ? [latestDraft] : []);
  const [draftEditor, setDraftEditor] = useState('');
  const [draftBusy, setDraftBusy] = useState(false);

  // Messages state
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesOffset, setMessagesOffset] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const MESSAGE_LIMIT = 50;

  // Timeline state
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Tags state
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [showAddTag, setShowAddTag] = useState(false);
  const [tagActionLoading, setTagActionLoading] = useState<number | null>(null);
  const [trustBusy, setTrustBusy] = useState(false);

  // Load full contact detail on mount
  useEffect(() => {
    async function loadDetail() {
      try {
        const full = await contactsAPI.get(contact.id);
        setDetailContact(full);
      } catch (err) {
        console.error('Failed to load contact detail:', err);
      }
    }
    loadDetail();
  }, [contact.id]);

  useEffect(() => {
    setCurrentNudge(nudge);
  }, [nudge?.id, nudge?.status]);

  useEffect(() => {
    async function loadDrafts() {
      try {
        const rows = await draftsAPI.list(contact.id, true);
        setDraftHistory(rows);
        if (rows[0]) setDraftEditor(rows[0].content);
      } catch (err) {
        console.error('Failed to load drafts:', err);
      }
    }
    void loadDrafts();
  }, [contact.id]);

  // Load messages when tab switches to messages
  useEffect(() => {
    if (activeTab !== 'messages' || messages.length > 0) return;
    loadMessages(0);
  }, [activeTab]);

  // Load timeline when tab switches
  useEffect(() => {
    if (activeTab !== 'timeline' || timeline.length > 0) return;
    loadTimeline();
  }, [activeTab]);

  // Load all tags when Add Tag is shown
  useEffect(() => {
    if (!showAddTag || allTags.length > 0) return;
    setTagsLoading(true);
    tagsAPI.list()
      .then(setAllTags)
      .catch(console.error)
      .finally(() => setTagsLoading(false));
  }, [showAddTag]);

  async function loadMessages(offset: number) {
    setMessagesLoading(true);
    try {
      const msgs = await contactsAPI.messages(contact.id, { limit: MESSAGE_LIMIT, offset });
      if (offset === 0) {
        setMessages(msgs);
      } else {
        setMessages(prev => [...prev, ...msgs]);
      }
      setMessagesOffset(offset + msgs.length);
      setHasMoreMessages(msgs.length === MESSAGE_LIMIT);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  }

  async function loadTimeline() {
    setTimelineLoading(true);
    try {
      const events = await contactsAPI.timeline(contact.id);
      setTimeline(events);
    } catch (err) {
      console.error('Failed to load timeline:', err);
    } finally {
      setTimelineLoading(false);
    }
  }

  async function handleAddTag(tagId: number) {
    setTagActionLoading(tagId);
    try {
      await contactsAPI.addTag(contact.id, tagId);
      // Refresh contact detail to get updated tags
      const updated = await contactsAPI.get(contact.id);
      setDetailContact(updated);
      if (onTagsChanged) onTagsChanged(updated);
      setShowAddTag(false);
    } catch (err) {
      console.error('Failed to add tag:', err);
    } finally {
      setTagActionLoading(null);
    }
  }

  async function handleRemoveTag(tagId: number) {
    setTagActionLoading(tagId);
    try {
      await contactsAPI.removeTag(contact.id, tagId);
      const updated = await contactsAPI.get(contact.id);
      setDetailContact(updated);
      if (onTagsChanged) onTagsChanged(updated);
    } catch (err) {
      console.error('Failed to remove tag:', err);
    } finally {
      setTagActionLoading(null);
    }
  }

  async function handleTrustLevelChange(trustLevel: TrustLevel) {
    try {
      setTrustBusy(true);
      await outboundAPI.updateTrustLevel(contact.id, trustLevel);
      const updated = { ...detailContact, trust_level: trustLevel };
      setDetailContact(updated);
      onTrustLevelChanged?.(updated);
    } catch (err) {
      console.error('Failed to update trust level:', err);
    } finally {
      setTrustBusy(false);
    }
  }

  const displayName = detailContact.display_name || detailContact.push_name || detailContact.phone;
  const relationship = detailContact.profile?.relationship || null;
  const relationshipStyle = getBadgeStyle(relationship);

  // Tags not yet assigned to this contact
  const assignedTagIds = new Set(detailContact.tags.map(t => t.id));
  const availableTags = allTags.filter(t => !assignedTagIds.has(t.id));

  const tabs: { id: DetailTab; label: string; icon: string }[] = [
    { id: 'profile', label: 'Profile', icon: 'person' },
    { id: 'messages', label: 'Messages', icon: 'chat' },
    { id: 'facts', label: 'Facts', icon: 'lightbulb' },
    { id: 'timeline', label: 'Timeline', icon: 'timeline' },
  ];

  const activeDraft = draftHistory.find((d) => d.status !== 'archived') || draftHistory[0] || null;

  async function regenerateDraft() {
    try {
      setDraftBusy(true);
      const row = await draftsAPI.regenerate(contact.id, currentNudge?.id);
      setDraftHistory((prev) => [row, ...prev.map((draft) => (draft.id === row.id ? draft : draft))]);
      setDraftEditor(row.content);
    } catch (err) {
      console.error('Failed to regenerate draft:', err);
    } finally {
      setDraftBusy(false);
    }
  }

  async function saveDraft() {
    if (!activeDraft) return;
    try {
      setDraftBusy(true);
      const updated = await draftsAPI.update(activeDraft.id, draftEditor);
      setDraftHistory((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
    } catch (err) {
      console.error('Failed to save draft:', err);
    } finally {
      setDraftBusy(false);
    }
  }

  async function queueDraft() {
    if (!activeDraft) return;
    try {
      setDraftBusy(true);
      await draftsAPI.queue(activeDraft.id);
    } catch (err) {
      console.error('Failed to queue draft:', err);
    } finally {
      setDraftBusy(false);
    }
  }

  async function snoozeCurrentNudge() {
    if (!currentNudge) return;
    try {
      const updated = await nudgesAPI.snooze(currentNudge.id, '3d');
      setCurrentNudge(updated);
    } catch (err) {
      console.error('Failed to snooze nudge:', err);
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Contact header */}
      <div className="px-4 pt-2 pb-3 border-b border-edge flex-shrink-0">
        <div className="flex items-start justify-between mb-1">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-ink truncate">{displayName}</h2>
            <p className="text-sm text-ink-secondary">{detailContact.phone}</p>
            <LanguageBadge
              contactId={detailContact.id}
              language={detailContact.language}
              detectedLanguage={detailContact.detected_language}
              onLanguageChanged={(lang) => {
                setDetailContact({ ...detailContact, language: lang });
              }}
            />
          </div>
          {relationship && (
            <span className={`px-3 py-1 rounded-full text-sm font-medium flex-shrink-0 ml-2 ${relationshipStyle.bg} ${relationshipStyle.text}`}>
              {relationshipStyle.label}
            </span>
          )}
        </div>

        <div className="mt-2 p-2 rounded-lg border border-edge bg-canvas">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-semibold text-ink uppercase tracking-wide">Trust level</p>
              <p className="text-[11px] text-ink-secondary">
                AI draft handling policy for this contact.
              </p>
            </div>
            <select
              value={detailContact.trust_level}
              onChange={(e) => void handleTrustLevelChange(e.target.value as TrustLevel)}
              disabled={trustBusy}
              className="text-xs rounded-md border border-edge bg-white px-2 py-1"
            >
              <option value="never_auto">Never auto-send</option>
              <option value="review_required">Review required</option>
              <option value="auto_send">Auto-send</option>
            </select>
          </div>
        </div>

        {/* Tracking override */}
        <div className="mt-2 p-2 rounded-lg border border-edge bg-canvas">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-semibold text-ink uppercase tracking-wide">Tracking</p>
              <p className="text-[11px] text-ink-secondary">
                {detailContact.tracking_override === null
                  ? 'Following global policy'
                  : detailContact.tracking_override
                    ? 'Manually tracked'
                    : 'Manually untracked'}
              </p>
            </div>
            <select
              value={
                detailContact.tracking_override === null
                  ? 'global'
                  : detailContact.tracking_override
                    ? 'tracked'
                    : 'untracked'
              }
              onChange={async (e) => {
                const val = e.target.value;
                const override = val === 'global' ? null : val === 'tracked';
                try {
                  const updated = await contactsAPI.updateTracking(detailContact.id, override);
                  setDetailContact({ ...detailContact, tracking_override: updated.tracking_override, is_tracked: updated.is_tracked });
                } catch {}
              }}
              className="text-xs rounded-md border border-edge bg-white px-2 py-1"
            >
              <option value="global">Follow global policy</option>
              <option value="tracked">Tracked</option>
              <option value="untracked">Untracked</option>
            </select>
          </div>
        </div>

        {/* Tags section */}
        <div className="mt-2">
          <div className="flex flex-wrap gap-1 items-center">
            {detailContact.tags.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: tag.color + '20', color: tag.color }}
              >
                {tag.icon && <span>{tag.icon}</span>}
                {tag.label}
                <button
                  onClick={() => handleRemoveTag(tag.id)}
                  disabled={tagActionLoading === tag.id}
                  className="ml-0.5 hover:opacity-70 transition-opacity"
                  aria-label={`Remove tag ${tag.label}`}
                >
                  {tagActionLoading === tag.id ? (
                    <span className="inline-block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span className="material-icons-round text-xs" style={{ fontSize: '12px' }}>close</span>
                  )}
                </button>
              </span>
            ))}
            <button
              onClick={() => setShowAddTag(!showAddTag)}
              className="px-2 py-0.5 rounded-full text-xs font-medium bg-canvas border border-edge hover:bg-canvas-secondary transition-colors text-ink-secondary"
            >
              + Add tag
            </button>
          </div>

          {/* Available tags to add */}
          {showAddTag && (
            <div className="mt-2 p-2 rounded-lg bg-canvas border border-edge">
              {tagsLoading ? (
                <p className="text-xs text-ink-tertiary">Loading tags...</p>
              ) : availableTags.length === 0 ? (
                <p className="text-xs text-ink-tertiary">No more tags to add</p>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {availableTags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => handleAddTag(tag.id)}
                      disabled={tagActionLoading === tag.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium hover:opacity-80 transition-opacity disabled:opacity-50"
                      style={{ backgroundColor: tag.color + '20', color: tag.color }}
                    >
                      {tag.icon && <span>{tag.icon}</span>}
                      {tag.label}
                    </button>
                  ))}
                </div>
              )}
              <button
                onClick={() => setShowAddTag(false)}
                className="mt-1 text-xs text-ink-tertiary hover:text-ink"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex border-b border-edge flex-shrink-0 overflow-x-auto no-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { captureEvent('contact_detail_section_viewed', { section: tab.id }); setActiveTab(tab.id); }}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
              activeTab === tab.id
                ? 'border-ink text-ink'
                : 'border-transparent text-ink-tertiary hover:text-ink-secondary'
            }`}
          >
            <span className="material-icons-round text-base">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className={`flex-1 ${activeTab === 'messages' ? 'overflow-hidden flex flex-col' : 'overflow-y-auto glass-scroll'}`}>
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="px-4 py-4 space-y-5">
            {currentNudge && (
              <div className="rounded-xl border border-warning/30 bg-warning-bg p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-warning">Follow-up nudge</p>
                    <p className="text-sm text-ink mt-1">{currentNudge.talking_point}</p>
                  </div>
                  <button
                    onClick={snoozeCurrentNudge}
                    className="text-xs px-2 py-1 rounded-md bg-white border border-edge hover:bg-canvas"
                  >
                    Snooze 3d
                  </button>
                </div>
              </div>
            )}

            {detailContact.profile?.summary ? (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="material-icons-round text-ink-secondary text-lg">summarize</span>
                  <h3 className="text-sm font-bold text-ink uppercase tracking-wide">AI Summary</h3>
                </div>
                <p className="text-ink leading-relaxed pl-7">{detailContact.profile.summary}</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <span className="material-icons-round text-ink-tertiary text-5xl mb-3 block">person_outline</span>
                <p className="text-ink-tertiary">No AI profile generated yet</p>
                <p className="text-xs text-ink-tertiary mt-1">Profile is generated after the contact sync pipeline runs</p>
              </div>
            )}

            {detailContact.last_message_at && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="material-icons-round text-ink-secondary text-lg">schedule</span>
                  <h3 className="text-sm font-bold text-ink uppercase tracking-wide">Last Message</h3>
                </div>
                <p className="text-ink-secondary text-sm pl-7">
                  {formatRelativeTime(detailContact.last_message_at)} ({new Date(detailContact.last_message_at).toLocaleString()})
                </p>
              </div>
            )}

            {/* Follow-up Draft */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-ink">Follow-up Draft</h3>
                <button
                  onClick={regenerateDraft}
                  disabled={draftBusy}
                  className="text-xs px-2 py-1 rounded-md bg-canvas border border-edge hover:bg-canvas-secondary disabled:opacity-50"
                >
                  Regenerate
                </button>
              </div>
              <textarea
                value={draftEditor}
                onChange={(e) => setDraftEditor(e.target.value)}
                rows={3}
                placeholder="Draft text will appear here"
                className="w-full px-3 py-2 rounded-lg border border-edge focus:outline-none focus:ring-2 focus:ring-accent/30 text-sm"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={saveDraft}
                  disabled={draftBusy || !activeDraft}
                  className="text-xs px-3 py-1.5 rounded-md bg-ink text-white disabled:opacity-50"
                >
                  Save edit
                </button>
                <button
                  onClick={() => navigator.clipboard?.writeText(draftEditor)}
                  className="text-xs px-3 py-1.5 rounded-md border border-edge bg-white hover:bg-canvas"
                >
                  Copy
                </button>
                <button
                  onClick={queueDraft}
                  disabled={draftBusy || !activeDraft}
                  className="text-xs px-3 py-1.5 rounded-md border border-edge bg-white hover:bg-canvas disabled:opacity-50"
                >
                  Queue intent
                </button>
              </div>
              {draftHistory.length > 1 && (
                <p className="text-xs text-ink-tertiary">{draftHistory.length} draft versions available</p>
              )}
            </div>

          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="flex flex-col" style={{ minHeight: '300px', height: '100%' }}>
            <div className="flex-1 px-4 py-4 overflow-y-auto glass-scroll">
              {messagesLoading && messages.length === 0 ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-edge border-t-ink-secondary rounded-full animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8">
                  <span className="material-icons-round text-ink-tertiary text-5xl mb-3 block">chat_bubble_outline</span>
                  <p className="text-ink-tertiary">No messages yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <MessageBubble
                      key={msg.id}
                      message={msg}
                      userLanguage={userLanguage}
                      contactLanguage={detailContact.language}
                    />
                  ))}

                  {hasMoreMessages && (
                    <div className="flex justify-center pt-2">
                      <button
                        onClick={() => loadMessages(messagesOffset)}
                        disabled={messagesLoading}
                        className="px-4 py-2 text-sm text-ink-secondary bg-canvas border border-edge rounded-lg hover:bg-canvas-secondary transition-colors disabled:opacity-50"
                      >
                        {messagesLoading ? 'Loading...' : 'Load more messages'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <MessageComposer
              contactId={detailContact.id}
              userLanguage={userLanguage}
              contactLanguage={detailContact.language}
              onSent={() => {
                loadMessages(0);
              }}
            />
          </div>
        )}

        {/* Facts Tab */}
        {activeTab === 'facts' && (
          <div className="px-4 py-4">
            {detailContact.facts && detailContact.facts.length > 0 ? (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="material-icons-round text-ink-secondary text-lg">lightbulb</span>
                  <h3 className="text-sm font-bold text-ink uppercase tracking-wide">Extracted Facts</h3>
                </div>
                <div className="pl-7 space-y-4">
                  {detailContact.facts.map((section, i) => (
                    <div key={i}>
                      <p className="text-xs text-ink-tertiary font-medium mb-1">{section.timestamp}</p>
                      <ul className="list-disc list-inside space-y-1">
                        {section.facts.map((fact, j) => (
                          <li key={j} className="text-sm text-ink leading-relaxed">{fact}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <span className="material-icons-round text-ink-tertiary text-5xl mb-3 block">lightbulb_outline</span>
                <p className="text-ink-tertiary">No facts extracted yet</p>
                <p className="text-xs text-ink-tertiary mt-1">Facts are extracted from conversations by the AI pipeline</p>
              </div>
            )}
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="px-4 py-4">
            {timelineLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-edge border-t-ink-secondary rounded-full animate-spin" />
              </div>
            ) : timeline.length === 0 ? (
              <div className="text-center py-8">
                <span className="material-icons-round text-ink-tertiary text-5xl mb-3 block">timeline</span>
                <p className="text-ink-tertiary">No timeline events yet</p>
              </div>
            ) : (
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-edge" />
                <div className="space-y-4">
                  {timeline.map((event) => (
                    <div key={event.id} className="flex gap-3 pl-10 relative">
                      {/* Dot */}
                      <div className="absolute left-3 top-1 w-2.5 h-2.5 rounded-full bg-white border-2 border-ink-secondary" />
                      <div className="flex-1 pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-ink">{event.title}</p>
                          <span className="text-xs text-ink-tertiary flex-shrink-0">
                            {formatRelativeTime(event.timestamp)}
                          </span>
                        </div>
                        {event.description && (
                          <p className="text-sm text-ink-secondary mt-0.5 leading-relaxed">{event.description}</p>
                        )}
                        <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs bg-canvas border border-edge text-ink-tertiary">
                          {event.event_type}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}

export default ContactDetailDrawer;
